import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { FileDown, FileText } from "lucide-react";
import { fetchShifts, fetchReport, fetchPublicShifts, fetchPublicReport } from "@/api";
import { exportReportPDF, exportReportWord } from "@/exportReport";

export default function Reports({ mine, publicMode = false, publicMineId = null }) {
  const [shifts, setShifts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState(null);
  const [logDumper, setLogDumper] = useState("ALL");

  const listShifts = useCallback(
    () => (publicMode ? fetchPublicShifts(publicMineId) : fetchShifts()),
    [publicMode, publicMineId]
  );
  const getReport = useCallback(
    (id) => (publicMode ? fetchPublicReport(id, publicMineId) : fetchReport(id)),
    [publicMode, publicMineId]
  );

  const load = useCallback(async () => {
    try {
      const list = await listShifts();
      setShifts(list);
      if (!selected && list.length > 0) setSelected(list[0].id);
    } catch (e) {
      console.error(e);
    }
  }, [selected, listShifts]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLogDumper("ALL");
    getReport(selected).then(setReport).catch(console.error);
  }, [selected, getReport]);

  const tripsByDumper = {};
  (report?.trip_log || []).forEach((t) => {
    (tripsByDumper[t.dumper_number] = tripsByDumper[t.dumper_number] || []).push(t.timestamp);
  });
  Object.values(tripsByDumper).forEach((a) => a.reverse());

  if (shifts.length === 0) {
    return (
      <div className="border-2 border-black bg-zinc-100 p-10 text-center hard-shadow" data-testid="no-reports-empty">
        <p className="font-head text-xl font-bold uppercase">No shifts yet</p>
        <p className="text-sm font-medium text-zinc-500 mt-1">Start a shift from the Shift Manager tab.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-screen">
      <div>
        <h3 className="font-head text-lg font-bold uppercase mb-3">Shifts</h3>
        <div className="flex flex-wrap gap-2" data-testid="shift-list">
          {shifts.map((s) => (
            <button
              key={s.id}
              data-testid={`shift-select-${s.id}`}
              onClick={() => setSelected(s.id)}
              className={`border-2 border-black px-4 py-3 font-head font-bold text-sm uppercase pressable hard-shadow-sm ${
                selected === s.id ? "bg-[#FFB800]" : "bg-white hover:bg-zinc-100"
              }`}
            >
              {s.name}
              <span className="block text-[10px] font-semibold text-zinc-600">
                {new Date(s.started_at).toLocaleDateString()}{" "}
                {s.status === "ACTIVE" ? "• LIVE" : "• CLOSED"}
              </span>
            </button>
          ))}
        </div>
      </div>

      {report && (
        <div className="space-y-6" data-testid="shift-report">
          <div className="flex gap-3 justify-end">
            <button
              data-testid="export-pdf-btn"
              onClick={() => {
                exportReportPDF(report, mine);
                toast.success("PDF downloaded");
              }}
              className="h-14 px-6 border-2 border-black bg-black text-[#FFB800] hard-shadow-sm pressable font-head text-base font-black uppercase flex items-center gap-2"
            >
              <FileDown size={18} strokeWidth={3} /> PDF
            </button>
            <button
              data-testid="export-word-btn"
              onClick={() => {
                exportReportWord(report, mine);
                toast.success("Word document downloaded");
              }}
              className="h-14 px-6 border-2 border-black bg-[#FFB800] hard-shadow-sm pressable font-head text-base font-black uppercase flex items-center gap-2"
            >
              <FileText size={18} strokeWidth={3} /> Word
            </button>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="border-2 border-black bg-black text-white px-5 py-3 hard-shadow">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#FFB800]">Total Trips</div>
              <div className="font-num text-6xl font-bold leading-none" data-testid="report-total-trips">
                {report.total_trips}
              </div>
            </div>
            <div className="border-2 border-black bg-white px-5 py-3 hard-shadow">
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Coal Trips</div>
              <div className="font-num text-6xl font-bold leading-none" data-testid="report-coal-trips">
                {report.coal_trips}
              </div>
            </div>
            <div className="border-2 border-black bg-white px-5 py-3 hard-shadow">
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">OB Trips</div>
              <div className="font-num text-6xl font-bold leading-none" data-testid="report-ob-trips">
                {report.ob_trips}
              </div>
            </div>
          </div>

          <div className="border-2 border-black bg-white hard-shadow p-4">
            <h3 className="font-head text-lg font-bold uppercase mb-3">Trips per Shovel</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-2 border-black border-collapse text-left" data-testid="per-shovel-table">
                <thead>
                  <tr className="bg-black text-white">
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Shovel</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Operator</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Material</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Dumpers</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Trips</th>
                  </tr>
                </thead>
                <tbody>
                  {report.per_shovel.map((s) => (
                    <tr key={s.shovel_id} data-testid={`shovel-row-${s.shovel_number}`}>
                      <td className="border border-black px-3 py-2 font-head text-xl font-black">{s.shovel_number}</td>
                      <td className="border border-black px-3 py-2 font-semibold">{s.shovel_operator || "—"}</td>
                      <td className="border border-black px-3 py-2">
                        <span className={`text-xs font-bold uppercase px-2 py-0.5 border-2 border-black ${
                          s.material === "COAL" ? "bg-black text-white" : "bg-[#FFB800]"
                        }`}>
                          {s.material}
                        </span>
                      </td>
                      <td className="border border-black px-3 py-2 font-semibold text-sm">{s.dumpers.join(", ")}</td>
                      <td className="border border-black px-3 py-2 font-num text-3xl font-bold">{s.trips}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border-2 border-black bg-white hard-shadow p-4">
            <h3 className="font-head text-lg font-bold uppercase mb-3">Trips per Dumper</h3>            <div className="overflow-x-auto">
              <table className="w-full border-2 border-black border-collapse text-left" data-testid="per-dumper-table">
                <thead>
                  <tr className="bg-black text-white">
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Dumper</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Operator</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Shovel</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Material</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Trips</th>
                  </tr>
                </thead>
                <tbody>
                  {report.per_dumper.map((d) => (
                    <tr key={d.dumper_id} data-testid={`dumper-row-${d.dumper_number}`}>
                      <td className="border border-black px-3 py-2 font-head text-xl font-black">{d.dumper_number}</td>
                      <td className="border border-black px-3 py-2 font-semibold">{d.dumper_operator || "—"}</td>
                      <td className="border border-black px-3 py-2 font-semibold">{d.shovel_number}</td>
                      <td className="border border-black px-3 py-2">
                        <span className={`text-xs font-bold uppercase px-2 py-0.5 border-2 border-black ${
                          d.material === "COAL" ? "bg-black text-white" : "bg-[#FFB800]"
                        }`}>
                          {d.material}
                        </span>
                      </td>
                      <td className="border border-black px-3 py-2 font-num text-3xl font-bold">{d.trips}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="border-2 border-black bg-white hard-shadow p-4">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <h3 className="font-head text-lg font-bold uppercase">Trip Log</h3>
              <select
                data-testid="trip-log-dumper-filter"
                value={logDumper}
                onChange={(e) => setLogDumper(e.target.value)}
                className="h-12 border-2 border-black px-3 font-bold bg-white"
              >
                <option value="ALL">All Dumpers</option>
                {report.per_dumper.map((d) => (
                  <option key={d.dumper_id} value={d.dumper_number}>
                    {d.dumper_number}{d.dumper_operator ? ` — ${d.dumper_operator}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full border-2 border-black border-collapse text-left" data-testid="trip-log-table">
                <thead>
                  <tr className="bg-black text-white">
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">#</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Time</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Dumper</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Operator</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Shovel</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Material</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.trip_log || [])
                    .filter((t) => logDumper === "ALL" || t.dumper_number === logDumper)
                    .map((t, i, arr) => (
                    <tr key={i} data-testid={`trip-log-row-${i}`}>
                      <td className="border border-black px-3 py-2 font-num text-xl font-bold">{arr.length - i}</td>
                      <td className="border border-black px-3 py-2 font-num text-xl font-bold" data-testid={`trip-log-time-${i}`}>
                        {new Date(t.timestamp).toLocaleTimeString()}
                      </td>
                      <td className="border border-black px-3 py-2 font-head text-xl font-black">{t.dumper_number}</td>
                      <td className="border border-black px-3 py-2 font-semibold">{t.dumper_operator || "—"}</td>
                      <td className="border border-black px-3 py-2 font-semibold">{t.shovel_number}</td>
                      <td className="border border-black px-3 py-2">
                        <span className={`text-xs font-bold uppercase px-2 py-0.5 border-2 border-black ${
                          t.material === "COAL" ? "bg-black text-white" : "bg-[#FFB800]"
                        }`}>
                          {t.material}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(report.trip_log || []).filter((t) => logDumper === "ALL" || t.dumper_number === logDumper).length === 0 && (
                    <tr>
                      <td colSpan={6} className="border border-black px-3 py-4 text-center text-sm font-medium text-zinc-500">
                        No trips recorded yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="border-2 border-black bg-white hard-shadow p-4">
            <h3 className="font-head text-lg font-bold uppercase mb-3">Shovel-wise Dumper Trip Times</h3>
            <div className="space-y-6">
              {report.per_shovel.map((s) => {
                const rows = report.per_dumper.filter((d) => d.shovel_number === s.shovel_number);
                const maxT = Math.max(0, ...rows.map((d) => (tripsByDumper[d.dumper_number] || []).length));
                return (
                  <div key={s.shovel_id} data-testid={`shovel-times-card-${s.shovel_number}`}>
                    <div className="flex items-center gap-2 border-b-2 border-black pb-1 mb-2 flex-wrap">
                      <span className="font-head font-bold uppercase text-sm">Shovel {s.shovel_number}</span>
                      {s.shovel_operator && <span className="text-xs font-semibold text-zinc-500">{s.shovel_operator}</span>}
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border-2 border-black ${
                        s.material === "COAL" ? "bg-black text-white" : "bg-[#FFB800]"
                      }`}>
                        {s.material}
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-2 border-black border-collapse text-left" data-testid={`shovel-times-table-${s.shovel_number}`}>
                        <thead>
                          <tr className="bg-black text-white">
                            <th className="border border-black px-3 py-2 font-head text-sm uppercase">Dumper</th>
                            <th className="border border-black px-3 py-2 font-head text-sm uppercase">Operator</th>
                            {Array.from({ length: maxT }).map((_, i) => (
                              <th key={i} className="border border-black px-3 py-2 font-head text-sm uppercase">Trip {i + 1}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((d) => (
                            <tr key={d.dumper_id}>
                              <td className="border border-black px-3 py-2 font-head text-lg font-black">{d.dumper_number}</td>
                              <td className="border border-black px-3 py-2 font-semibold text-sm">{d.dumper_operator || "—"}</td>
                              {Array.from({ length: maxT }).map((_, i) => (
                                <td
                                  key={i}
                                  className="border border-black px-3 py-2 font-num text-lg font-bold"
                                  data-testid={`shovel-times-cell-${d.dumper_number}-${i + 1}`}
                                >
                                  {tripsByDumper[d.dumper_number]?.[i]
                                    ? new Date(tripsByDumper[d.dumper_number][i]).toLocaleTimeString()
                                    : "—"}
                                </td>
                              ))}
                            </tr>
                          ))}
                          {maxT === 0 && (
                            <tr>
                              <td colSpan={2} className="border border-black px-3 py-3 text-center text-sm font-medium text-zinc-500">
                                No trips yet
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
